import React, { useEffect } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Boxes,
  CalendarClock,
  Check,
  ChartGantt,
  ChartNoAxesCombined,
  FileSpreadsheet,
  Tags,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavHeader from "../components/layout/NavHeader";
import ProductionScheduleDemo from "../components/marketing/ProductionScheduleDemo";
import PublicFooter from "../components/layout/PublicFooter";
import UpdatesSignup from "../components/marketing/UpdatesSignup";
import Seo from "../components/seo/Seo";
import { LIST_PRICE_LABELS } from "../content/listPricing";
import { blogPosts } from "../content/blogPosts.generated";
import { trackEvent } from "../lib/analytics";
import styles from "./Home.module.css";

// Rendered as the visible FAQ and as FAQPage JSON-LD from the same strings.
const FAQ_KEYS = ["who", "cost", "how", "changeover", "erp", "labels"] as const;

const WORKFLOW_STEPS = ["orders", "scheduling", "production", "traceability", "fulfillment"] as const;
const CURRENT_VALUE_PROOFS = ["planning", "connection", "traceability"] as const;

// Curated for B2B buyers: software fit, production pain, and technical expertise.
// Resolve against the generated published-only feed to avoid linking to drafts.
const FEATURED_BLOG_SLUGS = [
  "aps-vs-erp-vs-spreadsheets",
  "changeover-time-explained",
  "estimating-line-speed-capacity",
] as const;
const featuredBlogPosts = FEATURED_BLOG_SLUGS.flatMap((slug) => {
  const post = blogPosts.find((candidate) => candidate.slug === slug);
  return post ? [post] : [];
});

const HeroBrandVisual: React.FC = () => (
  <div className={styles.heroVisual} aria-hidden="true">
    <img
      src="/runmark-hdpe-extrusion-hero.jpg"
      alt=""
      className={styles.heroImage}
      fetchPriority="high"
    />
    <div className={styles.heroImageShade} />
    <div className={styles.heroPattern}>
      <span />
      <span />
      <span />
      <span />
    </div>
  </div>
);

const FeaturePreview = ({ type }: { type: string }) => {
  if (type === "constraints") {
    return (
      <div className={styles.constraintPreview} aria-hidden="true">
        <span><Check size={12} /> Line 2 eligible</span>
        <span>86 ft/min</span>
        <span>45m changeover</span>
      </div>
    );
  }
  if (type === "timeline") {
    return (
      <div className={styles.timelinePreview} aria-hidden="true">
        <span style={{ width: "46%" }}>HDPE 8 in</span>
        <span style={{ width: "31%" }}>PVC 4 in</span>
        <i />
      </div>
    );
  }
  if (type === "excel") {
    return (
      <div className={styles.sheetPreview} aria-hidden="true">
        <span>SO-1842</span><span>HDPE-8-SDR11</span><b>Matched</b>
        <span>SO-1844</span><span>PVC-PROFILE-GR</span><b>Matched</b>
      </div>
    );
  }
  if (type === "labels") {
    return (
      <div className={styles.labelMiniPreview} aria-hidden="true">
        <div><strong>HDPE PIPE</strong><span>Run 1026 · Line 1</span><span>Unit 08 / 24</span></div>
        <i />
      </div>
    );
  }
  if (type === "inventory") {
    return (
      <div className={styles.inventoryPreview} aria-hidden="true">
        <span>Order quantity <b>120 reels</b></span>
        <span>From inventory <b>40</b></span>
        <span>Schedule <b>80</b></span>
      </div>
    );
  }
  return (
    <div className={styles.reportPreview} aria-hidden="true">
      {[72, 94, 83, 101, 88].map((height, index) => (
        <span key={height} style={{ height: `${height}%` }}><i>{index + 1}</i></span>
      ))}
      <b>Scheduled vs. actual</b>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────
const Home: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  const trackTrialClick = (placement: "hero" | "how_it_works" | "footer") => {
    trackEvent("trial_cta_clicked", { placement });
  };

  const trackToolClick = (tool: "scheduling_playground" | "roi_calculator" | "readiness_assessment") => {
    trackEvent("tool_cta_clicked", { tool, placement: "homepage_tools" });
  };

  const faqs = FAQ_KEYS.map((key) => ({
    key,
    question: t(`pages.home.faq.${key}Question`),
    answer: t(`pages.home.faq.${key}Answer`, LIST_PRICE_LABELS),
  }));
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <>
      <Seo
        title={t('pages.home.seo.title')}
        description={t('pages.home.seo.description')}
        path="/"
        structuredData={structuredData}
      />
      <main id="main-content" className={styles.page}>
        <NavHeader variant="dark" />

        {/* ── Hero ── */}
        <section className={styles.hero}>
          <HeroBrandVisual />
          <div className={styles.heroContent}>
            <div className={styles.heroBadge}>
              <span className={styles.heroBadgeDot} />
              {t('pages.home.hero.badge')}
            </div>

            <h1 className={styles.heroHeadline}>
              {t('pages.home.hero.headline1')}<br />
              <em>{t('pages.home.hero.headline2')}</em>
            </h1>

            <p className={styles.heroSub}>
              {t('pages.home.hero.subtitle')}
            </p>

            <div className={styles.heroActions}>
              <Link
                to="/register"
                className={styles.btnPrimary}
                onClick={() => trackTrialClick("hero")}
              >
                {t('pages.home.hero.btnTrial')} →
              </Link>
              <a href="#product" className={styles.btnSecondary}>
                {t('pages.home.hero.btnPricing')}
              </a>
            </div>

            <p className={styles.heroTrial}>
              {t('common.trial.promise')}
            </p>
          </div>

        </section>

        <section className={styles.brandRail} aria-label="RunMark platform capabilities">
          <span>BUILT FOR THE PRODUCTION FLOOR</span>
          <span>01 / PLAN</span>
          <span>02 / SCHEDULE</span>
          <span>03 / TRACE</span>
          <span>04 / DELIVER</span>
        </section>

        <ProductionScheduleDemo />

        {/* ── Social Proof ── */}
        <div className={styles.socialProof}>
          <span className={styles.socialProofLabel}>{t('pages.home.socialProof.label')}</span>
          <div className={styles.socialProofLogos}>
            {[
              t('pages.home.socialProof.item1'),
              t('pages.home.socialProof.item2'),
              t('pages.home.socialProof.item3'),
              t('pages.home.socialProof.item4'),
              t('pages.home.socialProof.item5'),
              t('pages.home.socialProof.item6'),
            ].map(v => (
              <span key={v} className={styles.socialProofLogo}>{v}</span>
            ))}
          </div>
        </div>

        {/* ── How It Works ── */}
        <section id="product" className={styles.section}>
          <p className={styles.sectionLabel}>{t('pages.home.howItWorks.label')}</p>
          <h2 className={styles.sectionHeadline}>
            {t('pages.home.howItWorks.headline1')}<br />
            <em>{t('pages.home.howItWorks.headline2')}</em>
          </h2>
          <p className={styles.sectionSub}>
            {t('pages.home.howItWorks.subtitle')}
          </p>
          <p className={styles.pillarLink}>
            <Link to="/production-scheduling-software">
              Learn how production scheduling software works <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </p>

          <ul className={styles.connectedProofs} aria-label={t('pages.home.howItWorks.proofLabel')}>
            {CURRENT_VALUE_PROOFS.map((proof) => (
              <li key={proof}>
                <span className={styles.connectedProofIcon} aria-hidden="true">
                  <Check size={15} strokeWidth={2.4} />
                </span>
                <span>{t(`pages.home.howItWorks.proofs.${proof}`)}</span>
              </li>
            ))}
          </ul>

          <div className={styles.workflow}>
            {WORKFLOW_STEPS.map((step, index) => (
              <div className={styles.workflowStep} key={step}>
                <div className={styles.workflowIndex}>{String(index + 1).padStart(2, "0")}</div>
                <h3>{t(`pages.home.howItWorks.${step}.title`)}</h3>
                <p>{t(`pages.home.howItWorks.${step}.body`)}</p>
                {index < WORKFLOW_STEPS.length - 1 && <ArrowRight aria-hidden="true" />}
              </div>
            ))}
          </div>

          <div className={styles.scopePanel}>
            <div>
              <p className={styles.scopeLabel}>{t("pages.home.howItWorks.scopeLabel")}</p>
              <h3>{t("pages.home.howItWorks.scopeTitle")}</h3>
            </div>
            <p>{t("pages.home.howItWorks.scopeBody")}</p>
          </div>

          <div className={styles.sectionCta}>
            <Link
              to="/register"
              className={styles.btnPrimary}
              onClick={() => trackTrialClick("how_it_works")}
            >
              {t('pages.home.howItWorks.btnTrial')} →
            </Link>
            <span>{t('common.trial.promise')}</span>
          </div>
        </section>

        {/* ── Features ── */}
        <div className={styles.featuresBg}>
          <section className={styles.featuresSection}>
            <p className={styles.sectionLabel}>{t('pages.home.features.label')}</p>
            <h2 className={styles.sectionHeadline}>
              {t('pages.home.features.headline1')}<br />
              <em>{t('pages.home.features.headline2')}</em>
            </h2>
            <p className={styles.sectionSub}>
              {t('pages.home.features.subtitle')}
            </p>

            <div className={styles.featureGrid}>
              {[
                {
                  titleKey: 'pages.home.features.feature1.title',
                  bodyKey: 'pages.home.features.feature1.body',
                  icon: CalendarClock,
                  flagship: true,
                  preview: 'constraints',
                },
                {
                  titleKey: 'pages.home.features.feature2.title',
                  bodyKey: 'pages.home.features.feature2.body',
                  icon: ChartGantt,
                  preview: 'timeline',
                },
                {
                  titleKey: 'pages.home.features.feature3.title',
                  bodyKey: 'pages.home.features.feature3.body',
                  icon: FileSpreadsheet,
                  preview: 'excel',
                },
                {
                  titleKey: 'pages.home.features.feature4.title',
                  bodyKey: 'pages.home.features.feature4.body',
                  icon: Tags,
                  preview: 'labels',
                },
                {
                  titleKey: 'pages.home.features.feature5.title',
                  bodyKey: 'pages.home.features.feature5.body',
                  icon: Boxes,
                  preview: 'inventory',
                },
                {
                  titleKey: 'pages.home.features.feature6.title',
                  bodyKey: 'pages.home.features.feature6.body',
                  icon: ChartNoAxesCombined,
                  preview: 'reporting',
                },
              ].map((f) => {
                const Icon = f.icon;
                const body = (
                  <>
                    <div className={styles.featureIcon} aria-hidden="true">
                      <Icon size={19} strokeWidth={1.8} />
                    </div>
                    <h3 className={styles.featureTitle}>{t(f.titleKey)}</h3>
                    <p className={styles.featureBody}>{t(f.bodyKey)}</p>
                    <FeaturePreview type={f.preview} />
                  </>
                );
                return (
                  <div
                    key={f.titleKey}
                    className={f.flagship ? `${styles.feature} ${styles.featureFlagship}` : styles.feature}
                  >
                    {f.flagship ? <div className={styles.featureFlagshipText}>{body}</div> : body}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* ── Old Way vs RunMark ── */}
        <section className={styles.section}>
          <p className={styles.sectionLabel}>{t('pages.home.comparison.label')}</p>
          <h2 className={styles.sectionHeadline}>
            {t('pages.home.comparison.headline1')}<br />
            <em>{t('pages.home.comparison.headline2')}</em>
          </h2>
          <p className={styles.sectionSub}>
            {t('pages.home.comparison.subtitle')}
          </p>

          <div className={styles.comparison}>
            <div className={`${styles.comparisonCol} ${styles.comparisonOld}`}>
              <h3 className={styles.comparisonTitle}>{t('pages.home.comparison.old.title')}</h3>
              <ul className={styles.comparisonList}>
                {[1, 2, 3, 4, 5].map(n => (
                  <li key={n}>
                    <span className={styles.comparisonMarkOld}>✕</span>
                    {t(`pages.home.comparison.old.item${n}`)}
                  </li>
                ))}
              </ul>
            </div>
            <div className={`${styles.comparisonCol} ${styles.comparisonNew}`}>
              <h3 className={styles.comparisonTitle}>{t('pages.home.comparison.runmark.title')}</h3>
              <ul className={styles.comparisonList}>
                {[1, 2, 3, 4, 5].map(n => (
                  <li key={n}>
                    <span className={styles.comparisonMarkNew}>✓</span>
                    {t(`pages.home.comparison.runmark.item${n}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── Tools ── */}
        <section className={styles.toolsSection} aria-labelledby="tools-heading">
          <div className={styles.toolsIntro}>
            <p className={styles.sectionLabel}>{t("pages.home.tools.label")}</p>
            <h2 id="tools-heading" className={styles.sectionHeadline}>{t("pages.home.tools.title")}</h2>
            <p>{t("pages.home.tools.subtitle")}</p>
          </div>
          <div className={styles.toolsGrid}>
            <a
              className={styles.primaryTool}
              href="https://demo.runmark.app"
              onClick={() => trackToolClick("scheduling_playground")}
            >
              <span className={styles.toolNumber}>01</span>
              <div>
                <p>{t("pages.home.tools.primaryEyebrow")}</p>
                <h3>{t("pages.home.tools.primaryTitle")}</h3>
                <span>{t("pages.home.tools.primaryBody")}</span>
              </div>
              <ArrowUpRight aria-hidden="true" />
            </a>
            <div className={styles.secondaryTools}>
              <a href="https://roi.runmark.app" onClick={() => trackToolClick("roi_calculator")}>
                <span className={styles.toolNumber}>02</span>
                <div><h3>{t("pages.home.tools.roiTitle")}</h3><p>{t("pages.home.tools.roiBody")}</p></div>
                <ArrowUpRight aria-hidden="true" />
              </a>
              <a href="https://assessment.runmark.app" onClick={() => trackToolClick("readiness_assessment")}>
                <span className={styles.toolNumber}>03</span>
                <div><h3>{t("pages.home.tools.assessmentTitle")}</h3><p>{t("pages.home.tools.assessmentBody")}</p></div>
                <ArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        {/* ── Who It's For ── */}
        <section id="industries" className={styles.section}>
          <p className={styles.sectionLabel}>{t('pages.home.whoItsFor.label')}</p>
          <h2 className={styles.sectionHeadline}>
            {t('pages.home.whoItsFor.headline1')}<br />
            <em>{t('pages.home.whoItsFor.headline2')}</em>
          </h2>
          <p className={styles.sectionSub}>
            {t('pages.home.whoItsFor.subtitle')}
          </p>

          <div className={styles.verticals}>
            {[
              { titleKey: 'pages.home.whoItsFor.vertical1.title', bodyKey: 'pages.home.whoItsFor.vertical1.body', to: '/industries/pipe-extrusion-scheduling' },
              { titleKey: 'pages.home.whoItsFor.vertical2.title', bodyKey: 'pages.home.whoItsFor.vertical2.body', to: '/industries/profile-extrusion-scheduling' },
              { titleKey: 'pages.home.whoItsFor.vertical3.title', bodyKey: 'pages.home.whoItsFor.vertical3.body', to: '/industries/tube-conduit-extrusion-scheduling' },
              { titleKey: 'pages.home.whoItsFor.vertical4.title', bodyKey: 'pages.home.whoItsFor.vertical4.body', to: '/industries' },
              { titleKey: 'pages.home.whoItsFor.vertical5.title', bodyKey: 'pages.home.whoItsFor.vertical5.body', to: '/industries' },
              { titleKey: 'pages.home.whoItsFor.vertical6.title', bodyKey: 'pages.home.whoItsFor.vertical6.body', to: '/industries' },
            ].map((v, i) => (
              <Link key={v.titleKey} to={v.to} className={styles.verticalRow}>
                <span className={styles.verticalIndex}>{String(i + 1).padStart(2, "0")}</span>
                <h3 className={styles.verticalTitle}>{t(v.titleKey)}</h3>
                <p className={styles.verticalBody}>{t(v.bodyKey)}</p>
                <ArrowRight className={styles.verticalArrow} aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className={styles.section} aria-labelledby="faq-heading">
          <p className={styles.sectionLabel}>FAQ</p>
          <h2 id="faq-heading" className={styles.sectionHeadline}>{t('pages.home.faq.title')}</h2>
          <div className={styles.faqList}>
            {faqs.map((faq) => (
              <details key={faq.key} className={styles.faqItem}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Latest Blog Posts ── */}
        {featuredBlogPosts.length > 0 && (
          <section className={styles.blogSection} aria-labelledby="home-blog-heading">
            <div className={styles.blogHeader}>
              <div>
                <p className={styles.sectionLabel}>From the RunMark Blog</p>
                <h2 id="home-blog-heading" className={styles.sectionHeadline}>Insights from the production floor.</h2>
                <p className={styles.blogIntro}>Practical advice on production scheduling, changeovers, and traceability.</p>
              </div>
              <Link className={styles.blogAll} to="/blog">
                View all articles <ArrowRight size={17} aria-hidden="true" />
              </Link>
            </div>
            <div className={styles.blogGrid}>
              {featuredBlogPosts.map((post) => (
                <article className={styles.blogCard} key={post.slug}>
                  <time className={styles.blogDate} dateTime={post.date}>
                    {new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${post.date}T12:00:00Z`))}
                  </time>
                  <h3>
                    <Link
                      to={`/blog/${post.slug}`}
                      onClick={() => trackEvent("homepage_blog_post_clicked", { slug: post.slug })}
                    >
                      {post.title}
                    </Link>
                  </h3>
                  <p>{post.description}</p>
                  <Link
                    className={styles.blogReadMore}
                    to={`/blog/${post.slug}`}
                    onClick={() => trackEvent("homepage_blog_post_clicked", { slug: post.slug })}
                  >
                    Read article <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ── CTA Banner ── */}
        <div className={styles.ctaBanner}>
          <div className={styles.ctaBannerInner}>
            <h2 className={styles.ctaHeadline}>
              {t('pages.home.cta.headline1')}<br />
              <em>{t('pages.home.cta.headline2')}</em>
            </h2>
            <p className={styles.ctaSub}>
              {t('pages.home.cta.subtitle')}
            </p>
            <div className={styles.ctaActions}>
              <Link
                to="/register"
                className={styles.btnWhite}
                onClick={() => trackTrialClick("footer")}
              >
                {t('pages.home.cta.btnTrial')} →
              </Link>
              <Link to="/contact" className={styles.btnGhost}>
                {t('pages.home.cta.btnContact')}
              </Link>
            </div>
            <p className={styles.ctaTrial}>
              {t('common.trial.promise')}
            </p>
            <UpdatesSignup source="home" className={styles.ctaUpdates} />
          </div>
        </div>

      </main>
      <PublicFooter showReviewBadges />
    </>
  );
};

export default Home;
